import {startsWith} from 'lodash';
import React, {lazy, ReactElement, useContext, useEffect} from 'react';
import {Route, Switch} from 'react-router-dom';
import {EmptyHeaderWithSignOutButton} from 'shared/components/empty-header';
import {ImpostorModeContext} from 'src/components/ImpostorModeContext';
import StudentRootApp, {StudentRootAppProps} from 'src/components/StudentRootApp';
import Survey from 'src/components/Survey';
import withSuspense from 'src/hooks/withSuspense';
import {buildRoutes, cleanConfigs} from 'src/utils/routes';
import ModalRoutes from 'src/views/transfer/ModalRoutes';
import Navbar from 'src/views/transfer/Navbar';
import PortfolioSidebar from 'transfer-student/portfolio-sidebar/components/portfolio-sidebar';
import EarningsSidebarPages from '../../components/earnings-sidebar-pages';
import {withEducationPhase} from '../../hooks/with-education-phase';
import {EducationPhase} from '../../types/enums';
import {useDispatch} from 'react-redux';
import {buildNotificationBundlesEvent} from 'shared/notification-center/actions/notification-bundle';

const TransferAccountSettings = lazy(
  () => import('src/views/transfer/AccountSettings/TransferAccountSettings')
);
const CollegeProfile = lazy(() => import('transfer-student/college-profile/containers'));
const CopyrightPolicy = lazy(() => import('src/components/copyright-policy'));
const DataViewHistory = lazy(() => import('src/components/data-view-history-page'));
const PrivacyPolicy = lazy(() => import('src/components/privacy-policy'));
const NotificationCenter = lazy(() => import('shared/notification-center'));
const Rules = lazy(() => import('student-rules/components/rules'));
const Sitemap = lazy(() => import('src/components/Sitemap'));
const StudentMessages = lazy(() => import('src/components/StudentMessages'));
const TermsOfUse = lazy(() => import('src/components/TermsOfUse'));
const TabbedCollegeSearch = lazy(() => import('src/components/CollegeSearch/TabbedCollegeSearch'));
const Unsubscribe = lazy(() => import('src/components/Unsubscribe/Unsubscribe'));
const Videos = lazy(() => import('src/components/Videos'));

const StudentSitemap = withSuspense(Sitemap, {type: 'Student'});
const TransferEarningsSidebarPages = withEducationPhase(
  EarningsSidebarPages,
  EducationPhase.COMMUNITY_COLLEGE
);
const handleHistoryChange: StudentRootAppProps['onHistoryChange'] = (path) => {
  const onPage = {
    collegeProfile: startsWith(path, '/edu/'),
    colleges: startsWith(path, '/colleges'),
    portfolio: startsWith(path, '/portfolio'),
    homePage: startsWith(path, '/home'),
    checklistPage: startsWith(path, '/checklist'),
    accountSettings: startsWith(path, '/account-settings'),
    notificationCenter: startsWith(path, '/notification-center'),
    messages: startsWith(path, '/messages'),
  };

  const hasContainer = !(
    onPage.colleges ||
    onPage.collegeProfile ||
    onPage.portfolio ||
    onPage.accountSettings ||
    onPage.messages ||
    onPage.homePage ||
    onPage.checklistPage
  );
  const hasBackground = onPage.collegeProfile || onPage.notificationCenter;
  return {
    hasBackground,
    hasContainer,
  };
};

export default function TransferRootApp(): ReactElement | null {
  const impostorModeContext = useContext(ImpostorModeContext);
  const dispatch = useDispatch();

  useEffect(() => dispatch(buildNotificationBundlesEvent()), [dispatch]);

  const appRoutes = buildRoutes(
    cleanConfigs([
      {path: '/terms-of-use', component: TermsOfUse},
      {path: '/students/rules', component: Rules},
      {path: '/privacy-policy', component: PrivacyPolicy},
      {path: '/copyright_policy', component: CopyrightPolicy},
      impostorModeContext.isImpostor && {
        path: '/students/connections',
        to: '/portfolio',
        as: 'redirect',
      },
      {path: '/colleges', component: TabbedCollegeSearch},
      {path: '/colleges-who-have-viewed-me', component: DataViewHistory},
      {path: '/edu/:slug', component: CollegeProfile},
      {path: '/following', component: PortfolioSidebar},
      {path: ['/portfolio', '/home', '/checklist'], component: TransferEarningsSidebarPages},
      {path: '/videos', component: Videos},
      {path: '/messages/:messageId?', component: StudentMessages},
      {path: '/notification-center', component: NotificationCenter},
      {path: '/notification-center-refresh', to: '/notification-center', as: 'redirect'},
      {path: '/transfers/settings', to: '/account-settings', as: 'redirect'},
      {path: '/sitemap', component: Sitemap, exact: true},
      {path: '/sitemap/students', component: StudentSitemap, exact: true},
      {path: '/account-settings', component: TransferAccountSettings},
      {path: '/survey/:id/:page?', component: Survey},
      {path: '/unsubscribe', component: Unsubscribe},
      {as: 'redirect', to: '/home'},
    ])
  );

  return (
    <StudentRootApp
      onHistoryChange={handleHistoryChange}
      appModals={<ModalRoutes />}
      disableImpostorToolbar={false}
      appNavbar={
        <Switch>
          <Route path="/survey" component={EmptyHeaderWithSignOutButton} />
          <Route path="*" component={Navbar} />
        </Switch>
      }
      appRoutes={<Switch>{appRoutes}</Switch>}
    />
  );
}
